import { useState, useRef, useCallback } from 'react'

export type SelectionData = { text: string; from: number; to: number; head: number }

type UseSelectionTrackingParams = {
  isEditInProgress: boolean
  /** Fires on any mousedown outside inline-edit UI when no edit is in progress.
   *  Used by the facade to clear transient highlights (e.g., after insert below). */
  onDismiss?: () => void
}

/**
 * Detects when the user finalizes a text selection (mouseup / shift+arrow)
 * or clicks outside the editor. Pure DOM — no ProseMirror, no Redux.
 *
 * Returns the most recently committed selection (or null if cleared).
 * The consuming hook is responsible for interpreting the selection
 * (computing anchors, dispatching to Redux, etc.).
 */
export function useSelectionTracking({ isEditInProgress, onDismiss }: UseSelectionTrackingParams) {
  const [committedSelection, setCommittedSelection] = useState<SelectionData | null>(null)
  const pendingSelection = useRef<SelectionData | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)

  // Store isEditInProgress in a ref so the event handlers always read the current value
  const isEditInProgressRef = useRef(isEditInProgress)
  isEditInProgressRef.current = isEditInProgress

  // Store onDismiss in a ref to keep containerRef's dependency array stable
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss

  const handleSelectionChange = useCallback((sel: SelectionData | null) => {
    pendingSelection.current = sel
  }, [])

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (cleanupRef.current) {
      cleanupRef.current()
      cleanupRef.current = null
    }

    if (!node) return

    const commitSelection = () => {
      requestAnimationFrame(() => {
        if (isEditInProgressRef.current) return

        const sel = pendingSelection.current
        const hasRealSelection = sel && sel.from !== sel.to && sel.text.trim().length > 0

        if (hasRealSelection) {
          setCommittedSelection(sel)
        } else {
          setCommittedSelection(null)
        }
      })
    }

    let mouseDownInEditor = false

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      const isInlineEditUI = target instanceof Element && target.closest('[data-inline-edit]')
      mouseDownInEditor = node.contains(target) && !isInlineEditUI

      // Dismiss transient highlights on any real user interaction (inside or outside editor)
      if (!isEditInProgressRef.current && !isInlineEditUI) {
        onDismissRef.current?.()
      }

      if (!mouseDownInEditor) {
        // Clear selection when clicking outside the editor, inline-edit UI, and dialogs
        if (isEditInProgressRef.current) return
        const isDialog = target instanceof Element && (target.closest('[role="dialog"]') || target.closest('[data-radix-portal]'))
        if (!isInlineEditUI && !isDialog) {
          setCommittedSelection(null)
        }
      }
    }

    const handleMouseUp = () => {
      if (mouseDownInEditor) {
        commitSelection()
      }
      mouseDownInEditor = false
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.shiftKey || e.key === 'Shift') {
        commitSelection()
      }
    }

    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mouseup', handleMouseUp)
    node.addEventListener('keyup', handleKeyUp)

    cleanupRef.current = () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mouseup', handleMouseUp)
      node.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  return { committedSelection, containerRef, handleSelectionChange }
}
